// src/api/base.js
/**
 * БАЗОВЫЕ УТИЛИТЫ И HTTP-КЛИЕНТ ДЛЯ API
 * Используется и публичным клиентом, и админским.
 */

const API_BASE_ENV = (process.env.REACT_APP_API_BASE || '').trim();

function resolveApiBase() {
  if (API_BASE_ENV) return API_BASE_ENV;
  if (process.env.NODE_ENV === 'production') {
    throw new Error('REACT_APP_API_BASE is not set in .env');
  }
  // eslint-disable-next-line no-console
  console.warn('[api] REACT_APP_API_BASE is not set. Using relative /api (dev fallback).');
  return '/api';
}

export const API_BASE = resolveApiBase();

/* ===== ПУБЛИЧНЫЙ ORIGIN ДЛЯ МЕДИА (НОРМАЛИЗАЦИЯ URL) ===== */
const API_URL = (() => {
  try { return new URL(API_BASE, window.location.href); }
  catch { return new URL('/api', window.location.href); }
})();
const API_ORIGIN = API_URL.origin;
export const PUBLIC_ORIGIN = (process.env.REACT_APP_PUBLIC_ORIGIN || API_ORIGIN).replace(/\/$/, "");

/** Приводит url/thumbnail_url/display_url к корректным абсолютным ссылкам единого origin. */
export function normalizeMediaUrl(u) {
  if (!u) return null;
  try {
    const tmp = new URL(u, PUBLIC_ORIGIN); // работает для абсолютных и относительных
    const isLocal = tmp.hostname === 'localhost' || tmp.hostname === '127.0.0.1';

    // Срежем случайный /api перед /uploads
    const path = tmp.pathname.replace(/^\/api(?=\/uploads(\/|$))/, '');

    // Если локальный хост или это /uploads с «чужим» origin — пришиваем к PUBLIC_ORIGIN
    if (isLocal || (path.startsWith('/uploads') && tmp.origin !== PUBLIC_ORIGIN)) {
      return `${PUBLIC_ORIGIN}${path}${tmp.search || ''}${tmp.hash || ''}`;
    }
    return `${tmp.origin}${path}${tmp.search || ''}${tmp.hash || ''}`;
  } catch {
    // Строка без схемы и домена
    const path = (u.startsWith('/') ? u : `/${u}`).replace(/^\/api(?=\/uploads(\/|$))/, '');
    return `${PUBLIC_ORIGIN}${path}`;
  }
}

export function normalizeMediaItem(item = {}) {
  return {
    ...item,
    url:           normalizeMediaUrl(item.url),
    thumbnail_url: normalizeMediaUrl(item.thumbnail_url),
    display_url:   normalizeMediaUrl(item.display_url),
  };
}

/* ===================== ВСПОМОГАТЕЛЬНЫЕ ===================== */

export function buildQuery(params = {}) {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  return entries.length ? `?${entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&')}` : '';
}

/* ===== Белый список параметров /properties ===== */
const ALLOWED_PROPERTY_PARAMS = new Set([
  'region_id','district_id',
  'property_type_id','deal_type_id',
  'property_type','deal_type',
  'bedrooms','bedrooms_min','bedrooms_max',
  'price_min','price_max',
  'price_m2_min','price_m2_max',
  'area_min','area_max',
  'installment_available',
  'handover_quarter','handover_year','is_featured','is_investment',
  'sort','page','page_size','currency','locale'
]);

export function pickAllowed(obj = {}, allow = ALLOWED_PROPERTY_PARAMS){
  const out = {};
  for (const [k,v] of Object.entries(obj || {})) {
    if (allow.has(k) && v !== undefined && v !== null && v !== '') out[k] = v;
  }
  return out;
}

/* ===== Умный фоллбэк для прессы ===== */
export async function firstOk(paths) {
  let lastErr;
  for (const path of paths) {
    try {
      return await api(path);
    } catch (e) {
      lastErr = e;
      if (e && e.status === 404) continue;
      throw e;
    }
  }
  throw lastErr || new Error('No suitable endpoint found');
}
export function normalizeListPayload(data) {
  const raw = Array.isArray(data) ? data : (data?.items || data?.posts || data?.news || []);
  return Array.isArray(raw) ? raw : [];
}
export function normalizeOnePayload(data) {
  return data?.item || data?.post || data?.news || data?.article || data?.media || data;
}

/* ===================== ХРАНЕНИЕ ТОКЕНОВ (админ) ===================== */
function getStoredToken() {
  try {
    const raw = localStorage.getItem('adminToken');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed.token || null) : raw;
  } catch {
    return localStorage.getItem('adminToken') || null;
  }
}
function setStoredToken(t) {
  try { localStorage.setItem('adminToken', t || ''); } catch {}
}
function clearStoredToken() {
  try { localStorage.removeItem('adminToken'); } catch {}
}
function getStoredRefresh() {
  try { return localStorage.getItem('adminRefresh') || ''; } catch { return ''; }
}

let refreshingPromise = null;

async function refreshAccessToken() {
  if (refreshingPromise) return refreshingPromise;

  const refresh = getStoredRefresh();
  if (!refresh) throw new Error('No refresh token');

  refreshingPromise = fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${refresh}`,
    },
    mode: 'cors',
  })
  .then(async res => {
    const ct = res.headers.get('content-type') || '';
    const isJson = ct.includes('application/json');
    const data = isJson ? await res.json().catch(() => null) : await res.text();

    if (!res.ok) {
      const message = (isJson && (data?.message || data?.error || data?.msg)) || `HTTP ${res.status} ${res.statusText}`;
      const err = new Error(message);
      err.status = res.status;
      err.payload = data;
      throw err;
    }
    const newAccess = data?.access_token;
    if (!newAccess) throw new Error('Refresh succeeded but access_token missing');
    setStoredToken(newAccess);
    return newAccess;
  })
  .finally(() => { refreshingPromise = null; });

  return refreshingPromise;
}

function isExpiredJwtError(payload) {
  const msg = (payload?.msg || payload?.message || payload?.error || '').toString();
  const s = msg.toLowerCase();
  return s.includes('token has expired') || s.includes('expired') || s.includes('signature has expired');
}

/* ===================== БАЗОВЫЕ ФЕТЧИ ===================== */

export async function api(path, { method = 'GET', body, token, headers = {}, credentials } = {}) {
  const tok = token ?? getStoredToken();
  const finalHeaders = {
    Accept: 'application/json',
    ...(body ? { 'Content-Type': 'application/json' } : {}),
    ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
    ...headers,
  };

  const doFetch = async (overrideToken) => {
    const h = { ...finalHeaders };
    if (overrideToken) h.Authorization = `Bearer ${overrideToken}`;
    const res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: h,
      body: body ? JSON.stringify(body) : undefined,
      mode: 'cors',
      ...(credentials ? { credentials } : {}),
    });
    const ct = res.headers.get('content-type') || '';
    const isJson = ct.includes('application/json');
    const data = isJson ? await res.json().catch(() => null) : await res.text();
    return { res, data, isJson };
  };

  // 1-й запрос
  let { res, data, isJson } = await doFetch();

  // Если 401 и похоже на «access expired» — пробуем refresh + повтор
  if (!res.ok && res.status === 401 && isExpiredJwtError(data)) {
    try {
      const newAccess = await refreshAccessToken();
      ({ res, data, isJson } = await doFetch(newAccess));
    } catch {
      clearStoredToken();
    }
  }

  if (!res.ok) {
    const message = (isJson && data && (data.message || data.error || data.msg)) || `HTTP ${res.status} ${res.statusText}`;
    const err = new Error(message);
    err.status = res.status;
    err.payload = data;
    throw err;
  }
  return data;
}

export async function apiForm(path, formData, { method = 'POST', token, headers = {}, credentials } = {}) {
  const tok = token ?? getStoredToken();
  const finalHeaders = {
    Accept: 'application/json',
    ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
    ...headers,
  };

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: finalHeaders,
    body: formData,
    mode: 'cors',
    ...(credentials ? { credentials } : {}),
  });

  const ct = res.headers.get('content-type') || '';
  const isJson = ct.includes('application/json');
  const data = isJson ? await res.json().catch(() => null) : await res.text();

  if (!res.ok) {
    const message = (isJson && data && (data.message || data.error || data.msg)) || `HTTP ${res.status} ${res.statusText}`;
    const err = new Error(message);
    err.status = res.status;
    err.payload = data;
    throw err;
  }
  return data;
}

const Base = {
  API_BASE,
  PUBLIC_ORIGIN,
  buildQuery,
  pickAllowed,
  firstOk,
  normalizeListPayload,
  normalizeOnePayload,
  normalizeMediaUrl,
  normalizeMediaItem,
  api,
  apiForm,
};

export default Base;
